import React, { useEffect } from "react";

import { withoutReservedTags } from "@formant/core/common/withoutReservedTags";
import {
    ICommandParameterModel,
    parameterTypes,
} from "@formant/core/model/commandTemplate";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
    Button,
    FormControl,
    FormItem,
    FormLabel,
    Input,
    ITypeaheadOption,
    Label,
    Plus,
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Switch,
    Textarea,
    Trash,
    TypeaheadInput,
} from "@formant/ui-kit";

import { useStores } from "../../../../context/StoreContext";
import { convertTagsToTypeaheadOptions } from "../utils/convertTagsToTypeaheadOptions";
import {
    BooleanParameter,
    NumberParameter,
    OptionsParameter,
    StringParameter,
    ValidationParameters,
} from "./ParameterTypes";

interface ICommandModalParametersProps {
    parameters: Record<string, ICommandParameterModel>;
    updateParameter: (
        paramId: string,
        updates: Partial<ICommandParameterModel>,
    ) => void;
    removeParameter: (paramId: string) => void;
    addParameter: () => void;
}

const ParameterTypeComponent: Record<
    ICommandParameterModel["type"],
    React.FC<{
        param: ICommandParameterModel;
        onUpdate: (updates: Partial<ICommandParameterModel>) => void;
    }>
> = {
    string: StringParameter,
    number: NumberParameter,
    boolean: BooleanParameter,
    options: OptionsParameter,
};

export const CommandModalParameters: React.FC<ICommandModalParametersProps> = ({
    parameters,
    updateParameter,
    removeParameter,
    addParameter,
}) => {
    const { deviceStore } = useStores();

    const [existingTagsOptions, setExistingTagsOptions] = React.useState<
        ITypeaheadOption[]
    >([]);

    useEffect(() => {
        const tags = deviceStore.tags({ enabled: true, type: "default" }) || {};
        const typeaheadOptions: ITypeaheadOption[] = [];
        for (const key of Object.keys(withoutReservedTags(tags))) {
            tags[key].forEach(tag => {
                if (tag) {
                    typeaheadOptions.push({
                        key,
                        value: tag,
                    });
                }
                if (
                    !tag &&
                    !typeaheadOptions.find(option => option.key === key) &&
                    !typeaheadOptions.find(option => option.key === "*")
                ) {
                    typeaheadOptions.push({
                        key,
                        value: `*`,
                    });
                }
            });
        }
        setExistingTagsOptions(typeaheadOptions);
    }, [deviceStore]);

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <Button onClick={addParameter} type="button">
                    <Plus size={16} className="mr-2" />
                    Create Parameter
                </Button>
            </div>

            {Object.entries(parameters).length === 0 && (
                <div className="text-center py-12 rounded-md border">
                    No parameters configured. Add a parameter to get started.
                </div>
            )}

            <Accordion type="multiple">
                {Object.entries(parameters).map(([paramId, param]) => (
                    <AccordionItem value={paramId} key={paramId}>
                        <AccordionTrigger>
                            <div className="flex justify-between items-center">
                                <div className="flex-1">
                                    {param.name || "Untitled"}
                                </div>
                                <div>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => removeParameter(paramId)}
                                    >
                                        <Trash className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        </AccordionTrigger>
                        <AccordionContent>
                            <div className="flex justify-between items-center gap-4 space-y-4">
                                <div className="flex-1">
                                    <FormItem>
                                        <FormLabel>Name</FormLabel>
                                        <FormControl>
                                            <Input
                                                value={param.name}
                                                onChange={e =>
                                                    updateParameter(paramId, {
                                                        name: e.target.value,
                                                    })
                                                }
                                                placeholder="Parameter name"
                                            />
                                        </FormControl>
                                    </FormItem>
                                </div>

                                <div className="flex-1 !mt-0">
                                    <FormItem>
                                        <FormLabel>Type</FormLabel>
                                        <Select
                                            value={param.type}
                                            onValueChange={value => {
                                                const paramType =
                                                    value as ICommandParameterModel["type"];
                                                updateParameter(paramId, {
                                                    type: paramType,
                                                    value:
                                                        paramType === "boolean"
                                                            ? false
                                                            : paramType ===
                                                                "number"
                                                              ? 0
                                                              : "",
                                                });
                                            }}
                                        >
                                            <SelectTrigger>
                                                <SelectValue placeholder="Select type" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectGroup>
                                                    {parameterTypes.map(
                                                        type => (
                                                            <SelectItem
                                                                key={type}
                                                                value={type}
                                                            >
                                                                {type
                                                                    .charAt(0)
                                                                    .toUpperCase() +
                                                                    type.slice(
                                                                        1,
                                                                    )}
                                                            </SelectItem>
                                                        ),
                                                    )}
                                                </SelectGroup>
                                            </SelectContent>
                                        </Select>
                                    </FormItem>
                                </div>
                                <div className="flex-1 !mt-0">
                                    <FormItem>
                                        <FormLabel>Tags</FormLabel>
                                        <FormControl>
                                            <TypeaheadInput
                                                placeholder="Add tags"
                                                initialOptions={convertTagsToTypeaheadOptions(
                                                    param.tags || {},
                                                )}
                                                onOptionsChange={options =>
                                                    updateParameter(paramId, {
                                                        tags: options.reduce<
                                                            Record<
                                                                string,
                                                                string
                                                            >
                                                        >((acc, option) => {
                                                            acc[option.key] =
                                                                option.value;
                                                            return acc;
                                                        }, {}),
                                                    })
                                                }
                                                availableOptions={
                                                    existingTagsOptions
                                                }
                                            />
                                        </FormControl>
                                    </FormItem>
                                </div>
                            </div>
                            <div className="flex ">
                                <div className="flex-1">
                                    <FormItem>
                                        <FormLabel>Description</FormLabel>
                                        <FormControl>
                                            <Textarea
                                                value={param.description}
                                                onChange={e =>
                                                    updateParameter(paramId, {
                                                        description:
                                                            e.target.value,
                                                    })
                                                }
                                                placeholder="Parameter description"
                                            />
                                        </FormControl>
                                    </FormItem>
                                </div>
                            </div>

                            <div className="flex items-center justify-between py-4 gap-4">
                                <div className="flex-1">
                                    <FormItem>
                                        <div className="flex items-center space-x-2">
                                            <Switch
                                                id="required"
                                                checked={param.required}
                                                onCheckedChange={() => {
                                                    updateParameter(paramId, {
                                                        required:
                                                            !param.required,
                                                    });
                                                }}
                                            />
                                            <Label htmlFor="required">
                                                <FormLabel>Required</FormLabel>
                                            </Label>
                                        </div>
                                    </FormItem>
                                </div>
                                <div className="flex-1">
                                    <FormItem>
                                        <div className="flex items-center space-x-2">
                                            <Switch
                                                id="allowOverride"
                                                checked={param.allowOverride}
                                                onCheckedChange={() => {
                                                    updateParameter(paramId, {
                                                        allowOverride:
                                                            !param.allowOverride,
                                                    });
                                                }}
                                            />
                                            <Label htmlFor="allowOverride">
                                                <FormLabel>
                                                    Allow Override
                                                </FormLabel>
                                            </Label>
                                        </div>
                                    </FormItem>
                                </div>
                            </div>
                            {ParameterTypeComponent[param.type] && (
                                <div className="mt-4">
                                    {React.createElement(
                                        ParameterTypeComponent[param.type],
                                        {
                                            param,
                                            onUpdate: updates =>
                                                updateParameter(
                                                    paramId,
                                                    updates,
                                                ),
                                        },
                                    )}
                                </div>
                            )}
                            {param.type !== "options" && (
                                <div className="mt-4">
                                    <ValidationParameters
                                        param={param}
                                        onUpdate={updates =>
                                            updateParameter(paramId, updates)
                                        }
                                    />
                                </div>
                            )}
                        </AccordionContent>
                    </AccordionItem>
                ))}
            </Accordion>
        </div>
    );
};
